import React from 'react';
import { Button, Chip, Stack, Typography } from '@mui/material';
import moment from 'moment';
import CommonModal from '../../../common-components/CommonModal';
import { CommonTable, CommonTableColumn } from '../../../common-components/CommonTable';
import useSingleSessionViewHandler from '../hooks/useSingleSessionViewHandler';
import useUserSessions from '../hooks/useUserSessions';
import useUserSessionsListHandler from '../hooks/useUserSessionsListHandler';
import { ChatSession } from '../../MainLayout/slice/types';

interface UserSessionsListProps {
  userId: string | null;
}

export default function UserSessionsList({ userId }: UserSessionsListProps) {
  const { isLoading, userSessions } = useUserSessions(userId);
  const { setSelectedUserToView } = useUserSessionsListHandler();
  const { setSelectedSessionIsdToView } = useSingleSessionViewHandler();

  const handleClose = React.useCallback(() => {
    setSelectedUserToView(null);
  }, [setSelectedUserToView]);

  const handleViewTimeline = React.useCallback((sessionId: string) => {
    setSelectedUserToView(null);
    setSelectedSessionIsdToView(sessionId);
  }, [setSelectedSessionIsdToView, setSelectedUserToView]);

  const columns = React.useMemo<Array<CommonTableColumn<ChatSession>>>(
    () => [
      {
        key: 'title',
        header: 'Session',
        minWidth: 240,
        render: (session) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {session.title || 'Untitled session'}
          </Typography>
        ),
      },
      {
        key: 'type',
        header: 'Type',
        width: 120,
        render: (session) => <Chip size="small" label={session.sessionType ?? '—'} variant="outlined" />,
      },
      {
        key: 'createdAt',
        header: 'Created',
        width: 180,
        render: (session) => (
          <Typography variant="body2" color="text.secondary" noWrap>
            {session.createdAt ? moment(session.createdAt).format('MMM D, YYYY h:mm A') : '—'}
          </Typography>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        width: 140,
        render: (session) => (
          <Button size="small" variant="text" onClick={() => handleViewTimeline(session._id)}>
            View timeline
          </Button>
        ),
      },
    ],
    [handleViewTimeline],
  );

  return (
    <CommonModal open={Boolean(userId)} onClose={handleClose} title="User Sessions" maxWidth="lg">
      <Stack spacing={2} sx={{ minHeight: 320 }}>
        <Typography variant="body2" color="text.secondary">
          Sessions for the selected user.
        </Typography>
        <CommonTable<ChatSession>
          columns={columns}
          rows={userSessions}
          getRowId={(session) => session._id}
          isLoading={isLoading}
          emptyLabel="No sessions found for this user."
          containerSx={{ minHeight: 320 }}
        />
      </Stack>
    </CommonModal>
  );
}
